/*
 * CASA SOFIA — scroll reveal.
 *
 * Ports app/components/Reveal.tsx: ONE shared IntersectionObserver (instead of
 * one per element — the homepage has ~15) watches every `[data-reveal]` block.
 * On first intersection it adds `.is-visible` (base.css fades + lifts the block
 * into place, gated on `html.js .reveal`), applies any `data-reveal-delay` as a
 * transition-delay for staggering, then unobserves the element (one-shot).
 *
 * Progressive enhancement: the `js` class on <html> (set inline in theme.liquid;
 * we re-assert it here as the belt-and-braces second writer) is what arms the
 * opacity:0 start state, so no-JS / failed-boot clients render everything
 * visible. Under prefers-reduced-motion, or when IntersectionObserver is
 * missing, every block is marked visible immediately.
 *
 * `initReveal()` is invoked by global.js on DOMContentLoaded; it is idempotent
 * and also picks up any `[data-reveal]` injected later via a re-scan.
 */

const REVEAL_SELECTOR = '[data-reveal]';
let observer = null;

function reduceMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function show(el) {
  const delay = el.getAttribute('data-reveal-delay');
  if (delay) el.style.transitionDelay = `${delay}ms`;
  el.classList.add('is-visible');
}

function getObserver() {
  if (observer) return observer;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        show(entry.target);
        observer.unobserve(entry.target); // one-shot
      }
    },
    {threshold: 0.12, rootMargin: '0px 0px -8% 0px'},
  );
  return observer;
}

export function initReveal(root = document) {
  // Re-assert JS-capability so the reveal start state is armed even if the
  // inline writer in <head> was stripped/blocked.
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const els = root.querySelectorAll(REVEAL_SELECTOR);
  if (!els.length) return;

  // No IO support (or reduced motion): reveal everything up front.
  if (typeof IntersectionObserver === 'undefined' || reduceMotion()) {
    els.forEach(show);
    return;
  }

  const io = getObserver();
  els.forEach((el) => {
    if (el.classList.contains('is-visible')) return; // statically-visible hero
    io.observe(el);
  });
}
